import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

import { ErrorStrings, ValidationPatterns } from 'src/app/constants/form.constants';
import {
  User,
  UserAddress,
  UserAddressType,
  UserTelephone,
  UserTelephoneType,
} from 'src/app/interfaces/user';
import { UserService } from 'src/app/services/user/user.service';
import { Alert } from 'src/app/interfaces/alert';
import { HttpErrorResponse } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-user-details-confirmation',
  templateUrl: './user-details-confirmation.component.html',
  styleUrls: ['./user-details-confirmation.component.scss'],
})
export class RegisterUserDetailsConfirmationComponent implements OnInit {
  errorStrings = ErrorStrings;
  user: User;
  validationPatterns = ValidationPatterns;
  loading = false;
  alert: Alert = null;
  mainAddress: UserAddress;
  isUpdateDetails: boolean;
  canUpdateDetails = true;
  isBrowser: boolean;

  constructor(
    private router: Router,
    private userService: UserService,
    private route: ActivatedRoute,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.user = this.route.snapshot.data.user as User;
    this.isUpdateDetails = !!this.route.snapshot.data.isUpdateDetails;
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  save(): void {
    this.trackEvent('registerchip::yourdetails::submitted');
    this.closeAlert();
    this.loading = true;

    const userChanges = this.getPayloadToBeSent(this.user);

    this.userService
      .patchMe(userChanges)
      .subscribe(
        (updatedUser) => {
          this.user = updatedUser;
          this.mainAddress = this.getOrInitMainAddress(this.user);
          this.user.contact.telephone = this.getOrInitTelephones(this.user);

          this.alert = {
            type: 'success',
            message: 'Your details have been updated.',
          };
          if (this.isUpdateDetails) {
            this.router.navigate(['/account']);
          } else {
            this.router.navigate(['/claim-pet']);
          }
        },
        (err) => {
          let errorMessage = 'An error has occurred. Please try again later.';
          if (err instanceof HttpErrorResponse && err.status === 400) {
            if (err.error && err.error.error_code === 'invalid_postcode') {
              errorMessage =
                'Cannot save your address. Please ensure that you have provided a full valid UK postcode.';
            }
          }
          this.alert = {
            type: 'danger',
            message: errorMessage,
          };
        }
      )
      .add(() => {
        this.loading = false;
      });
  }

  private getOrInitMainAddress(user: User): UserAddress {
    let mainAddress = user.addresses.filter(
      (address) => address.addressType === UserAddressType.Main
    )[0];
    if (!mainAddress) {
      mainAddress = {
        line1: null,
        line2: null,
        town: null,
        county: null,
        postcode: null,
        addressType: UserAddressType.Main,
        temporary: null,
      };
      user.addresses.push(mainAddress);
    }
    return mainAddress;
  }

  private getOrInitTelephones(user: User): UserTelephone[] {
    const contactTelephones: UserTelephone[] = [];
    contactTelephones[0] = user.contact.telephone.filter(
      (telephone) => telephone.numberType === UserTelephoneType.Main
    )[0] || {
      number: '',
      numberType: UserTelephoneType.Main,
    };
    contactTelephones[1] = user.contact.telephone.filter(
      (telephone) => telephone.numberType === UserTelephoneType.Second
    )[0] || {
      number: '',
      numberType: UserTelephoneType.Second,
    };
    return contactTelephones;
  }

  // This is required to remove telephone contacts with empty string for phone numbers and generate a clean payload used
  // with user patching.
  private getPayloadToBeSent(user: User): Partial<User> {
    return {
      contact: {
        email: user.contact.email,
        telephone: user.contact.telephone.filter((telephone) => telephone.number),
      },
      addresses: user.addresses,
    };
  }

  closeAlert() {
    this.alert = null;
  }

  trackEvent(event: string) {
    if (!this.isBrowser) return;
    window.analytics.track(event);
  }

  ngOnInit(): void {
    this.mainAddress = this.getOrInitMainAddress(this.user);
    this.user.contact.telephone = this.getOrInitTelephones(this.user);

    if (
      this.isUpdateDetails &&
      this.userService.isUserBasic(this.user.productType) &&
      !this.user.detailsUpdatable &&
      !this.userService.isOlderThan14Days(this.user.created)
    ) {
      this.canUpdateDetails = false;
      this.router.navigate(['/account']);
    }
  }
}
